"use client";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Session } from "next-auth";
import { useSession } from "next-auth/react";
import Image from "next/image";
import { permanentRedirect, usePathname, useRouter } from "next/navigation";
import { Suspense, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useHeader } from "./hooks/data-api";
import { Search } from "./search-input";
import { Timezone } from "./timezone";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Skeleton } from "./ui/skeleton";
import { UITheme } from "@/provider/data/type";
import shTitle from "@/public/static/shareholder-title.png";
import searchIconBlue from "@/public/static/sh-icons/search-icon-blue.gif";
import { GrTest } from "react-icons/gr";

export function ShareHolder({
  form,
  showSearch = true,
}: {
  form: UseFormReturn<any>;
  showSearch?: boolean;
}) {
  const { data, isLoading } = useHeader();
  const { data: session, status } = useSession();
  const shareHolderUrl = process.env.NEXT_PUBLIC_O_AUTH_URL;
  const [text, setText] = useState<string>("");
  const router = useRouter();
  const pathname = usePathname();

  if (status === "unauthenticated" || session?.error == "InvalidAccessToken") {
    if (session?.error == "InvalidAccessToken") {
      permanentRedirect(
        `/login?callbackUrl=${encodeURIComponent(pathname)}&error=InvalidAccessToken`,
      );
    } else {
      permanentRedirect(`/login?callbackUrl=${encodeURIComponent(pathname)}`);
    }
  }

  return (
    <Suspense>
      <div
        className={cn(
          "mt-0 pt-0",
          isLoading
            ? `bg-gradient-to-t from-sh-shareholder2016-bottom to-sh-shareholder2016-top`
            : data?.theme === "moonlight"
              ? `bg-gradient-to-t from-sh-moonlight-bottom to-sh-moonlight-top`
              : data?.theme === "highcontrast"
                ? `bg-gradient-to-t from-sh-highcontrast-bottom to-sh-highcontrast-top`
                : `bg-gradient-to-t from-sh-shareholder2016-bottom to-sh-shareholder2016-top`,
        )}
      >
        <div className="h-18 ml-0 mr-6 flex flex-col py-1">
          {/* top */}
          <div className="flex w-full flex-row justify-between">
            {/* top-left */}
            <div className="flex">
              <a href={shareHolderUrl}>
                <Image
                  src={shTitle}
                  alt={"back to ShareHolder"}
                  width={240}
                  height={30}
                  className="h-6 w-[220px]"
                  unoptimized
                  priority
                />
              </a>
            </div>
            {/* top-right */}
            <div
              className={cn(
                "hidden flex-row lg:flex",
                data?.theme === "moonlight"
                  ? " text-blue-300"
                  : data?.theme === "highcontrast"
                    ? "text-green-200"
                    : "text-blue-950",
              )}
            >
              <div className="flex flex-col">
                <div className="mr-2 flex flex-row space-x-1">
                  <Timezone></Timezone>
                  <Timezone timeZone="Europe/London" label="London:"></Timezone>
                  <Timezone timeZone="CET" label="CET:"></Timezone>
                  <Timezone timeZone="America/New_York" label="NY:"></Timezone>
                  <Timezone
                    timeZone="America/Los_Angeles"
                    label="LA:"
                  ></Timezone>
                  <GrTest
                    size={10}
                    onClick={async () => {
                      // localhost:3000/micro-structure
                      let currentBaseUrl = window.location.href;
                      // localhost:3000
                      currentBaseUrl = currentBaseUrl.split("/")[2];
                      // http://localhost:3000
                      if (currentBaseUrl.includes("localhost")) {
                        currentBaseUrl = "http://" + currentBaseUrl;
                      } else {
                        currentBaseUrl = "https://" + currentBaseUrl;
                      }
                      window.open(
                        `${currentBaseUrl}/static/html-report/index.html`,
                        "_blank",
                        "width=800,height=600",
                      );
                    }}
                  />
                </div>
                {/* User greet */}
                <Greeting
                  session={session!}
                  status={status}
                  theme={data?.theme}
                ></Greeting>
              </div>
              <div
                className={cn(
                  "mb-0 ml-2 mr-0 mt-0.5 flex h-5 flex-row items-center p-0 ring-2",
                  data?.theme === "moonlight"
                    ? " ring-sh-moonlight-highlight"
                    : data?.theme === "highcontrast"
                      ? "ring-sh-highcontrast-highlight"
                      : "ring-sh-shareholder2016-highlight",
                )}
              >
                <div>
                  <Search
                    form={form}
                    className="h-4 w-[140px] rounded-none"
                    icon={false}
                    theme={data?.theme as UITheme}
                    showSearch={showSearch}
                  />
                </div>
                {/* Submit button */}
                <Button
                  type="submit"
                  className="m-0 ml-0.5 h-4 p-0"
                  variant={"ghost"}
                  title="Search"
                  disabled={form.formState.isSubmitting}
                >
                  <Image
                    className="m-0 h-4 w-4 p-0"
                    src={searchIconBlue}
                    height={4}
                    width={4}
                    alt="submit"
                    unoptimized
                  />
                </Button>
              </div>
            </div>
          </div>
          {/* bottom */}
          <div className="ml-2 mt-1 flex w-full flex-row justify-between">
            {/* bottom-left */}

            <div className="hidden flex-row items-center space-x-3 lg:flex">
              <div className="mr-3 flex">
                {isLoading ? (
                  <Skeleton
                    className={cn(
                      "h-4 w-[130px] rounded-none",
                      data?.theme === "moonlight"
                        ? " bg-sh-moonlight-base"
                        : data?.theme === "highcontrast"
                          ? "bg-sh-highcontrast-base"
                          : "bg-sh-shareholder2016-base",
                    )}
                  />
                ) : (
                  <Select
                    onValueChange={(v) => {
                      if (v === "go-feedback") {
                        permanentRedirect("/feedback");
                      }

                      const option = data?.goToList?.options.find(
                        (o) => o.key === v,
                      );
                      const redirectUrl =
                        option?.url.replace("~", shareHolderUrl ?? "") ?? "/";
                      permanentRedirect(redirectUrl);
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        "h-4 w-[130px] rounded-none pr-0 text-xsl",
                        data?.theme === "moonlight"
                          ? " bg-sh-moonlight-base text-white"
                          : data?.theme === "highcontrast"
                            ? "bg-sh-highcontrast-base text-black"
                            : "bg-sh-shareholder2016-base text-black",
                      )}
                    >
                      <SelectValue placeholder="Go to..." />
                    </SelectTrigger>
                    <SelectContent
                      className={cn(
                        "mx-0 max-h-[300px] w-[130px] overflow-y-scroll rounded-none p-0 text-xsl",
                        data?.theme === "moonlight"
                          ? " bg-sh-moonlight-base"
                          : data?.theme === "highcontrast"
                            ? "bg-sh-highcontrast-base"
                            : "bg-sh-shareholder2016-base",
                      )}
                    >
                      <SelectGroup className="m-0 p-0">
                        <SelectLabel
                          className={cn(
                            "m-0 p-0 text-left text-xsl",
                            data?.theme === "moonlight"
                              ? " text-white"
                              : data?.theme === "highcontrast"
                                ? "text-black"
                                : "text-black",
                          )}
                        >
                          Go to...
                        </SelectLabel>
                        {data?.goToList?.options
                          .sort((a, b) => a.sequence - b.sequence)
                          .map((option) => (
                            <SelectItem
                              key={option.key}
                              value={option.key}
                              className={cn(
                                "m-0 h-4 rounded-none p-0 px-1 text-left text-xsl focus:bg-blue-800 focus:text-white",
                                data?.theme === "moonlight"
                                  ? " text-white"
                                  : data?.theme === "highcontrast"
                                    ? "text-black"
                                    : "text-black",
                              )}
                            >
                              <p className="text-xsl">{option.description}</p>
                            </SelectItem>
                          ))}
                      </SelectGroup>
                      {session?.scopes?.includes("AISearchAdmin") && (
                        <SelectGroup className="m-0 p-0">
                          <SelectItem
                            key={"go-feedback"}
                            value={"go-feedback"}
                            className={cn(
                              "m-0 h-4 rounded-none p-0 px-1 text-left text-xsl focus:bg-blue-800 focus:text-white",
                              data?.theme === "moonlight"
                                ? " text-white"
                                : data?.theme === "highcontrast"
                                  ? "text-black"
                                  : "text-black",
                            )}
                          >
                            <p className="text-xsl">{"AI Feedback"}</p>
                          </SelectItem>
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="flex">
                {isLoading ? (
                  <Skeleton
                    className={cn(
                      "h-4 w-[130px] rounded-none",
                      data?.theme === "moonlight"
                        ? " bg-sh-moonlight-base"
                        : data?.theme === "highcontrast"
                          ? "bg-sh-highcontrast-base"
                          : "bg-sh-shareholder2016-base",
                    )}
                  />
                ) : (
                  <Select
                    onValueChange={(v) => {
                      const option = data?.addList?.options.find(
                        (o) => o.key === v,
                      );
                      const redirectUrl =
                        option?.url.replace("~", shareHolderUrl ?? "") ?? "/";
                      permanentRedirect(redirectUrl);
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        "h-4 w-[130px] rounded-none pr-0 text-xsl",
                        data?.theme === "moonlight"
                          ? " bg-sh-moonlight-base text-white"
                          : data?.theme === "highcontrast"
                            ? "bg-sh-highcontrast-base"
                            : "bg-sh-shareholder2016-base",
                      )}
                    >
                      <SelectValue placeholder="Add new..." />
                    </SelectTrigger>
                    <SelectContent
                      className={cn(
                        "mx-0 max-h-[300px] w-[130px] overflow-y-scroll rounded-none p-0 text-xsl",
                        data?.theme === "moonlight"
                          ? " bg-sh-moonlight-base"
                          : data?.theme === "highcontrast"
                            ? "bg-sh-highcontrast-base"
                            : "bg-sh-shareholder2016-base",
                      )}
                    >
                      <SelectGroup className="m-0 p-0">
                        <SelectLabel
                          className={cn(
                            "m-0 p-0 text-left text-xsl",
                            data?.theme === "moonlight"
                              ? " text-white"
                              : data?.theme === "highcontrast"
                                ? "text-black"
                                : "text-black",
                          )}
                        >
                          Add new...
                        </SelectLabel>
                        {data?.addList?.options
                          .sort((a, b) => a.sequence - b.sequence)
                          .map((option) => (
                            <SelectItem
                              key={option.key}
                              value={option.key}
                              className={cn(
                                "m-0 h-4 rounded-none p-0 px-1 text-left text-xsl text-black focus:bg-blue-800 focus:text-white",
                                data?.theme === "moonlight"
                                  ? " text-white"
                                  : data?.theme === "highcontrast"
                                    ? "text-black"
                                    : "text-black",
                              )}
                            >
                              <p className="text-xsl">{option.description}</p>
                            </SelectItem>
                          ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div
                className={cn(
                  "flex flex-row items-center ring-2",
                  data?.theme === "moonlight"
                    ? " ring-bg-sh-moonlight-highlight"
                    : data?.theme === "highcontrast"
                      ? "ring-bg-sh-highcontrast-highlight"
                      : "ring-bg-sh-shareholder2016-highlight",
                )}
              >
                <Input
                  className={cn(
                    "h-4 w-[180px] rounded-none placeholder:text-xsl",
                    data?.theme === "moonlight"
                      ? " bg-sh-moonlight-base text-white"
                      : data?.theme === "highcontrast"
                        ? "bg-sh-highcontrast-base text-black"
                        : "bg-sh-shareholder2016-base text-black",
                  )}
                  placeholder="Enter ticker or command"
                  type="text"
                  autoComplete="off"
                  value={text}
                  form="search-form"
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    console.log("submitting");
                    e.preventDefault();
                    const redirectUrl = `${shareHolderUrl}/CompanyPageLoader?ticker=${text}`;
                    router.push(redirectUrl);
                  }}
                />
                <Button
                  variant={"ghost"}
                  className="my-0 ml-1 mr-0 h-4 rounded-none p-0"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    console.log("clicking");
                    const redirectUrl = `${shareHolderUrl}/CompanyPageLoader?ticker=${text}`;
                    router.push(redirectUrl);
                  }}
                >
                  <Image
                    alt="search ticker in ShareHolder"
                    src={"/static/sh-icons/search-icon-blue.gif"}
                    width={4}
                    height={4}
                    className="m-0 h-4 w-4 p-0"
                    unoptimized
                  />
                </Button>
              </div>
            </div>
            {/* bottom-right */}
            <div className="hidden flex-row lg:flex">
              {isLoading ? (
                <Skeleton
                  className={cn(
                    "h-4 w-[230px] rounded-none",
                    data?.theme === "moonlight"
                      ? " bg-sh-moonlight-base"
                      : data?.theme === "highcontrast"
                        ? "bg-sh-highcontrast-base"
                        : "bg-sh-shareholder2016-base",
                  )}
                />
              ) : (
                <div className="flex flex-row space-x-1">
                  {data?.toolbar?.options
                    .sort((a, b) => a.sequence - b.sequence)
                    .map((option) => (
                      <a
                        key={option.key}
                        href={
                          option.newWindow
                            ? ""
                            : option?.url.replace("~", shareHolderUrl ?? "") ??
                              "/"
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          if (!option.newWindow) {
                            router.push(
                              option?.url.replace("~", shareHolderUrl ?? "") ??
                                "/",
                            );
                          } else {
                            window.open(
                              option?.url.replace("~", shareHolderUrl ?? "") ??
                                "/",
                              "newwindow",
                              `width=${option.windowWidth},height=${option.windowHeight}`,
                            );
                          }
                        }}
                      >
                        <Image
                          key={option.key}
                          src={`/static/sh-icons/${option.icon.toLowerCase()}`}
                          alt={option.description}
                          width={10}
                          height={10}
                          className="h-4 w-4"
                          unoptimized
                        />
                      </a>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Suspense>
  );
}

function Greeting(props: {
  status: "loading" | "authenticated" | "unauthenticated";
  session: Session;
  theme?: UITheme;
}) {
  return (
    <>
      <span className="items-end">
        {props?.status === "loading" ? (
          <div className="flex justify-end">
            <Skeleton
              className={cn(
                "h-4 w-[250px] rounded-none",
                props.theme === "moonlight"
                  ? " bg-sh-moonlight-base"
                  : props.theme === "highcontrast"
                    ? "bg-sh-highcontrast-base"
                    : "bg-sh-shareholder2016-base",
              )}
            />
          </div>
        ) : (
          <>
            <p className="text-right text-xsm font-medium">
              Logged in as <b>{props.session?.user?.name}</b> to{" "}
              <b className="underline">LFG</b>
            </p>
          </>
        )}
      </span>
    </>
  );
}
